// OBT F3-C — Personel (Formen) durumu: bir kampanya seçilir, kapsamınızdaki
// kişilerin atanan/cevaplanan/bekleyen/gecikme durumu listelenir. Kapsam
// backend'de: BM yalnız kendi Formenlerini görür (vekâlet genişletmez).
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Download, Users } from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtEmptyState, ObtStatTile } from '../ObtStates';
import { useCampaignReport } from './hooks/useCampaignReport';
import { useRespondentReport } from './hooks/useRespondentReport';
import { pendingParks } from './reportMetrics';
import { formatDuration, toCsv, downloadCsv } from './reportFormat';

function respondentCsv(rows) {
  const headers = ['Personel', 'Rol', 'Atanan park', 'Cevaplanan', 'Taslak', 'Bekleyen', 'Süresi geçti', 'Gecikme'];
  const body = rows.map((r) => [
    r.person?.name || '(bilinmiyor)',
    r.person?.role || '',
    r.responsible_park_count ?? 0,
    r.answered_park_count ?? 0,
    r.draft_park_count ?? 0,
    pendingParks(r),
    r.due_passed ? 'Evet' : 'Hayır',
    r.late_by_seconds ? formatDuration(r.late_by_seconds) : '—',
  ]);
  return toCsv(headers, body);
}

export default function RespondentPanel({ enabled = true }) {
  const campaignsRep = useCampaignReport({ enabled });
  const [campaignId, setCampaignId] = useState('');
  const resp = useRespondentReport(campaignId, { enabled: enabled && Boolean(campaignId) });

  const campaigns = campaignsRep.items;
  const rows = resp.pending;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-2 p-4">
          <Label htmlFor="resp-campaign">Bilgi Talebi</Label>
          <select
            id="resp-campaign"
            className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">Bir bilgi talebi seçin…</option>
            {campaigns.map((c) => (
              <option key={String(c.campaign_id)} value={c.campaign_id}>
                {c.title || 'Bilgi Talebi'}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400">
            Kişi başına revizyon sayısı ve ortalama cevap süresi backend'de bilgi talebi-personel
            kırılımında sağlanmaz; bu yüzden burada gösterilmez (uydurulmaz).
          </p>
        </CardContent>
      </Card>

      {!campaignId && (
        <ObtEmptyState title="Bilgi talebi seçilmedi" description="Personel durumunu görmek için bir bilgi talebi seçin." icon={Users} />
      )}
      {campaignId && resp.loading && <ObtLoading label="Personel durumu yükleniyor…" />}
      {campaignId && !resp.loading && resp.error && <ObtErrorState error={resp.error} onRetry={resp.reload} />}

      {campaignId && !resp.loading && !resp.error && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <ObtStatTile label="Kapsamdaki kişi" value={rows.length} icon={Users} tone="info" />
            <ObtStatTile label="Eksik cevaplı kişi" value={resp.pendingTotal} tone="warning" />
            <ObtStatTile label="Süresi geçen kişi" value={resp.lateTotal} tone="danger" />
          </div>

          <div className="flex items-center justify-end">
            <Button
              size="sm"
              variant="outline"
              disabled={rows.length === 0}
              onClick={() => downloadCsv('obt-personel-durumu.csv', respondentCsv(rows))}
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" /> CSV indir
            </Button>
          </div>

          {rows.length === 0 ? (
            <ObtEmptyState title="Eksik cevaplı kişi yok" description="Kapsamınızdaki herkes cevaplamış görünüyor." />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Personel</th>
                    <th className="px-3 py-2">Atanan park</th>
                    <th className="px-3 py-2">Cevaplanan</th>
                    <th className="px-3 py-2">Taslak</th>
                    <th className="px-3 py-2">Bekleyen</th>
                    <th className="px-3 py-2">Gecikme</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={String(r.person?.personnel_id || Math.random())} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {r.person?.name || '(bilinmiyor)'}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{r.responsible_park_count ?? 0}</td>
                      <td className="px-3 py-2 text-gray-700">{r.answered_park_count ?? 0}</td>
                      <td className="px-3 py-2 text-gray-700">{r.draft_park_count ?? 0}</td>
                      <td className="px-3 py-2 font-semibold text-amber-700">{pendingParks(r)}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {r.due_passed && r.late_by_seconds ? formatDuration(r.late_by_seconds) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
